import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';

export interface AppUser {
  id: string;
  clerk_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: string;
  supervisor_id: string | null;
  team_name: string | null;
  last_login_at: string | null;
  created_at: string | null;
}

/**
 * Phase 1: Settings > Users — list from D1 via /api/users (PRD SETT-003).
 * Search and filters (role, team) hit the API. Add user / edit role&team in a follow-up.
 */
export default function SettingsUsers() {
  const { getToken } = useAuth();
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState<string>('');
  const [filterTeam, setFilterTeam] = useState<string>('');
  const [users, setUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<AppUser | null>(null);
  const [editRole, setEditRole] = useState('');
  const [editTeam, setEditTeam] = useState('');
  const [saving, setSaving] = useState(false);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [inviteRole, setInviteRole] = useState('agent');
  const [inviteTeam, setInviteTeam] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    const token = await getToken();
    if (!token) {
      setError('Not signed in');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    if (filterRole) params.set('role', filterRole);
    if (filterTeam) params.set('team', filterTeam);
    if (search.trim()) params.set('search', search.trim());
    try {
      const base = typeof window !== 'undefined' ? window.location.origin : (import.meta.env.BASE_URL || '');
      const res = await fetch(`${base}/api/users?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setUsers(data.users ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [getToken, filterRole, filterTeam, search]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const displayName = (u: AppUser) =>
    [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email;

  const openEdit = (u: AppUser) => {
    setEditing(u);
    setEditRole(u.role);
    setEditTeam(u.team_name ?? '');
  };

  const closeEdit = () => {
    setEditing(null);
    setSaving(false);
  };

  const sendInvite = async () => {
    const email = inviteEmail.trim();
    if (!email) {
      setInviteError('Email is required');
      return;
    }
    const token = await getToken();
    if (!token) return;
    setInviteSending(true);
    setInviteError(null);
    const base = typeof window !== 'undefined' ? window.location.origin : '';
    try {
      const res = await fetch(`${base}/api/users/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          email,
          first_name: inviteFirstName.trim() || undefined,
          last_name: inviteLastName.trim() || undefined,
          role: inviteRole,
          team_name: inviteTeam.trim() || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setAddUserOpen(false);
      setInviteEmail('');
      setInviteFirstName('');
      setInviteLastName('');
      setInviteRole('agent');
      setInviteTeam('');
      fetchUsers();
    } catch (e) {
      setInviteError(e instanceof Error ? e.message : 'Failed to send invite');
    } finally {
      setInviteSending(false);
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    const token = await getToken();
    if (!token) return;
    setSaving(true);
    setError(null);
    const base = typeof window !== 'undefined' ? window.location.origin : '';
    try {
      const res = await fetch(`${base}/api/users/${editing.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ role: editRole, team_name: editTeam || null }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      closeEdit();
      fetchUsers();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>Users</h2>
      <p style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>
        Add user (sends Clerk invite), edit role and team/supervisor assignment, search and filter. Super Admin only.
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
        <input
          type="search"
          placeholder="Search by name or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ padding: '0.5rem 0.75rem', borderRadius: '6px', border: '1px solid var(--widget-bg)', minWidth: '200px' }}
        />
        <select
          value={filterRole}
          onChange={(e) => setFilterRole(e.target.value)}
          style={{ padding: '0.5rem 0.75rem', borderRadius: '6px', border: '1px solid var(--widget-bg)' }}
        >
          <option value="">All roles</option>
          <option value="agent">Agent</option>
          <option value="supervisor">Supervisor</option>
          <option value="admin">Admin</option>
          <option value="superadmin">Super Admin</option>
        </select>
        <select
          value={filterTeam}
          onChange={(e) => setFilterTeam(e.target.value)}
          style={{ padding: '0.5rem 0.75rem', borderRadius: '6px', border: '1px solid var(--widget-bg)' }}
        >
          <option value="">All teams</option>
          {Array.from(new Set(users.map((u) => u.team_name).filter(Boolean) as string[]))
            .sort()
            .map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
        </select>
      </div>

      {error && (
        <p style={{ color: 'var(--error)', marginBottom: '1rem' }}>{error}</p>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)', textAlign: 'left' }}>
              <th style={{ padding: '0.75rem' }}>Name</th>
              <th style={{ padding: '0.75rem' }}>Email</th>
              <th style={{ padding: '0.75rem' }}>Role</th>
              <th style={{ padding: '0.75rem' }}>Team / Supervisor</th>
              <th style={{ padding: '0.75rem' }}>Last login</th>
              <th style={{ padding: '0.75rem' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <td style={{ padding: '0.75rem', color: 'var(--text-muted)' }} colSpan={6}>
                  Loading…
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <td style={{ padding: '0.75rem', color: 'var(--text-muted)' }} colSpan={6}>
                  No users found. Add a user to get started.
                </td>
              </tr>
            ) : (
              users.map((u) => (
                <tr key={u.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  <td style={{ padding: '0.75rem' }}>{displayName(u)}</td>
                  <td style={{ padding: '0.75rem' }}>{u.email}</td>
                  <td style={{ padding: '0.75rem' }}>{u.role}</td>
                  <td style={{ padding: '0.75rem' }}>{u.team_name ?? '—'}</td>
                  <td style={{ padding: '0.75rem' }}>
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : '—'}
                  </td>
                  <td style={{ padding: '0.75rem' }}>
                    <button type="button" onClick={() => openEdit(u)} style={{ padding: '0.25rem 0.5rem', fontSize: '0.875rem' }}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <p style={{ marginTop: '1rem' }}>
        <button type="button" onClick={() => setAddUserOpen(true)} style={{ padding: '0.5rem 1rem', borderRadius: '6px', background: 'var(--accent)', color: 'white', border: 'none', cursor: 'pointer' }}>
          Add user
        </button>
      </p>

      {addUserOpen && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => !inviteSending && setAddUserOpen(false)}
        >
          <div
            style={{ background: 'var(--widget-bg)', borderRadius: '8px', padding: '1.5rem', minWidth: '320px', maxWidth: '90vw' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>Add user</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '1rem' }}>
              Sends a Clerk invitation email. Role and team are applied when they sign in.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <label>
                <span style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem' }}>Email *</span>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="user@example.com"
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid var(--widget-bg)', boxSizing: 'border-box' }}
                />
              </label>
              <label>
                <span style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem' }}>First name</span>
                <input
                  type="text"
                  value={inviteFirstName}
                  onChange={(e) => setInviteFirstName(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid var(--widget-bg)', boxSizing: 'border-box' }}
                />
              </label>
              <label>
                <span style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem' }}>Last name</span>
                <input
                  type="text"
                  value={inviteLastName}
                  onChange={(e) => setInviteLastName(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid var(--widget-bg)', boxSizing: 'border-box' }}
                />
              </label>
              <label>
                <span style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem' }}>Role</span>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid var(--widget-bg)' }}
                >
                  <option value="agent">Agent</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="admin">Admin</option>
                  <option value="superadmin">Super Admin</option>
                </select>
              </label>
              <label>
                <span style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem' }}>Team</span>
                <input
                  type="text"
                  value={inviteTeam}
                  onChange={(e) => setInviteTeam(e.target.value)}
                  placeholder="Optional"
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid var(--widget-bg)', boxSizing: 'border-box' }}
                />
              </label>
            </div>
            {inviteError && <p style={{ color: 'var(--error)', fontSize: '0.875rem', marginTop: '0.5rem' }}>{inviteError}</p>}
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.25rem', justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => !inviteSending && setAddUserOpen(false)} style={{ padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid var(--widget-bg)', background: 'transparent', color: 'var(--text)', cursor: 'pointer' }}>
                Cancel
              </button>
              <button type="button" onClick={sendInvite} disabled={inviteSending} style={{ padding: '0.5rem 1rem', borderRadius: '6px', background: 'var(--accent)', color: 'white', border: 'none', cursor: inviteSending ? 'not-allowed' : 'pointer' }}>
                {inviteSending ? 'Sending…' : 'Send invite'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={closeEdit}
        >
          <div
            style={{
              background: 'var(--widget-bg)',
              borderRadius: '8px',
              padding: '1.5rem',
              minWidth: '320px',
              maxWidth: '90vw',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>Edit user</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '1rem' }}>
              {displayName(editing)} ({editing.email})
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <label>
                <span style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem' }}>Role</span>
                <select
                  value={editRole}
                  onChange={(e) => setEditRole(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid var(--widget-bg)' }}
                >
                  <option value="agent">Agent</option>
                  <option value="supervisor">Supervisor</option>
                  <option value="admin">Admin</option>
                  <option value="superadmin">Super Admin</option>
                </select>
              </label>
              <label>
                <span style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem' }}>Team</span>
                <input
                  type="text"
                  value={editTeam}
                  onChange={(e) => setEditTeam(e.target.value)}
                  placeholder="Team name"
                  style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid var(--widget-bg)', boxSizing: 'border-box' }}
                />
              </label>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.25rem', justifyContent: 'flex-end' }}>
              <button type="button" onClick={closeEdit} style={{ padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid var(--widget-bg)', background: 'transparent', color: 'var(--text)', cursor: 'pointer' }}>
                Cancel
              </button>
              <button type="button" onClick={saveEdit} disabled={saving} style={{ padding: '0.5rem 1rem', borderRadius: '6px', background: 'var(--accent)', color: 'white', border: 'none', cursor: saving ? 'not-allowed' : 'pointer' }}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
